import {
  type EntityState,
  type FactState,
  isSegmentId,
  splitSegmentKey,
} from '@dl/shared';

/**
 * Pure model for the per-predicate **timeline strip** and the
 * **correct-vs-change** editor fork (ADR-0005 §3/§4, tracer #188).
 *
 * Valid-time segments of a `(subject, base-predicate)` live as separate op
 * chains: the bare predicate slot is the implicit first segment, explicit
 * successors are `<base>#seg_…` (minted by `fact.succeed`). This module folds
 * an `EntityState`'s `facts` map into the ordered, non-overlapping bar model
 * the strip renders — with INCLUSIVE labels ("…–1066", "1067–…") and a
 * NON-COLOR state cue so the strip is WCAG-AA without relying on hue.
 *
 * Progressive disclosure: a base with a single segment yields a one-bar model
 * flagged `single: true` so the editor can keep rendering exactly today's
 * single field and never surface the strip.
 */

/** One bar of the timeline strip: a single valid-time segment. */
export interface SegmentBar {
  /** The op-log key this segment folds from (`base` or `base#seg_…`). */
  key: string;
  /** Explicit segment id, or `null` for the bare-slot first segment. */
  segment: string | null;
  /** Inclusive lower bound (canonical day), or `null` = open start. */
  fromInclusive: number | null;
  /** Inclusive upper bound (canonical day), or `null` = open end. */
  toInclusive: number | null;
  /** The folded value of this segment (for the bar tooltip / label). */
  value: unknown;
  /**
   * Boundary shape cue — the a11y NON-COLOR signal (ADR-0005 UX, "text +
   * shape, never color alone"). `open-start` = "…–X"; `open-end` = "X–…";
   * `closed` = "X–Y"; `point` = a single-day segment.
   */
  shape: 'open-start' | 'open-end' | 'closed' | 'point';
}

/** The full strip model for one `(subject, base-predicate)`. */
export interface SegmentStripModel {
  base: string;
  bars: SegmentBar[];
  /** True when there is exactly one segment — render as today's single field. */
  single: boolean;
  /**
   * True when two bars overlap in valid time (a §5 consistency error). The
   * strip renders colliding bars + the caller raises the overlap advisory.
   */
  hasOverlap: boolean;
}

function shapeOf(from: number | null, to: number | null): SegmentBar['shape'] {
  if (from !== null && to !== null && from === to) return 'point';
  if (from === null && to !== null) return 'open-start';
  if (from !== null && to === null) return 'open-end';
  if (from === null && to === null) return 'open-end'; // fully timeless → treat as open
  return 'closed';
}

function latestValue(fact: FactState): unknown {
  return fact.versions[fact.versions.length - 1]?.value;
}

/**
 * Build the timeline-strip model for `base` on `entity`. Collects the bare
 * slot + every `<base>#seg_…` successor, sorts by start day ascending
 * (open-start first), and flags overlap.
 *
 * Excludes soft-deleted segments and belief / repeatable (`#op_`) keys —
 * those are not valid-time segments in S1.
 */
export function buildSegmentStrip(entity: EntityState, base: string): SegmentStripModel {
  const bars: SegmentBar[] = [];
  for (const [key, fact] of Object.entries(entity.facts)) {
    if (fact.soft_deleted) continue;
    const split = splitSegmentKey(key);
    // Bare slot for this base, or an explicit seg_ successor of this base.
    const isBareSlot = key === base;
    const isSegSuccessor = split.base === base && split.segment !== null && isSegmentId(split.segment);
    if (!isBareSlot && !isSegSuccessor) continue;
    const from = fact.validity?.from ?? null;
    const to = fact.validity?.to ?? null;
    bars.push({
      key,
      segment: isSegSuccessor ? split.segment : null,
      fromInclusive: from,
      // Stored `to` is half-open (exclusive — ADR-0005 §4); convert ONCE here
      // to the inclusive "through day to−1" the bar/label/overlap math uses
      // (cf. instant.ts `toInclusiveEnd`). This is the single display edge.
      toInclusive: to === null ? null : to - 1,
      value: latestValue(fact),
      shape: shapeOf(from, to),
    });
  }
  bars.sort((a, b) => startKey(a) - startKey(b));
  return {
    base,
    bars,
    single: bars.length <= 1,
    hasOverlap: detectOverlap(bars),
  };
}

function startKey(b: SegmentBar): number {
  return b.fromInclusive ?? Number.NEGATIVE_INFINITY;
}

/**
 * Two segments overlap when their inclusive bar windows (already converted
 * from half-open storage in {@link buildSegmentStrip}) share a day. Adjacent
 * segments — predecessor inclusive `to = X−1` (stored exclusive `X`),
 * successor `from = X` — do NOT overlap.
 */
function detectOverlap(bars: readonly SegmentBar[]): boolean {
  for (let i = 0; i < bars.length; i++) {
    for (let j = i + 1; j < bars.length; j++) {
      const a = bars[i]!;
      const b = bars[j]!;
      const aFrom = a.fromInclusive ?? Number.NEGATIVE_INFINITY;
      const aTo = a.toInclusive ?? Number.POSITIVE_INFINITY;
      const bFrom = b.fromInclusive ?? Number.NEGATIVE_INFINITY;
      const bTo = b.toInclusive ?? Number.POSITIVE_INFINITY;
      // inclusive overlap: aFrom <= bTo && bFrom <= aTo
      if (aFrom <= bTo && bFrom <= aTo) return true;
    }
  }
  return false;
}

/**
 * Format a single bar's INCLUSIVE label (ADR-0005 §4: author/display treat
 * `to` as inclusive). `fmt` converts a canonical day to a display string
 * (calendar-aware at the call site); `dash` is the locale's range dash.
 */
export function formatSegmentLabel(
  bar: SegmentBar,
  fmt: (day: number) => string,
  dash = '–',
): string {
  const from = bar.fromInclusive;
  const to = bar.toInclusive;
  if (from === null && to === null) return '';
  if (from !== null && to !== null) {
    return from === to ? fmt(from) : `${fmt(from)}${dash}${fmt(to)}`;
  }
  if (from === null) return `${dash}${fmt(to as number)}`;
  return `${fmt(from)}${dash}`;
}
